/**
 * offlineCache — thin read-through cache backed by @capacitor/preferences.
 *
 * Preferences has a web implementation (localStorage under the hood) so the
 * same code path works in the browser, PWA, and native. We stamp each entry
 * with a timestamp so callers can decide whether a cached value is fresh enough
 * to use proactively vs. only as an offline fallback.
 *
 * Usage pattern (read-through with offline fallback):
 *
 *   const sessions = await withCache(
 *     `sessions:${eventId}`,
 *     () => sessionService.getAll(eventId),
 *   );
 */
import { Preferences } from '@capacitor/preferences';

const PREFIX = 'cache:';

interface Envelope<T> {
  t: number;      // epoch ms when written
  v: T;
}

export async function cacheSet<T>(key: string, value: T): Promise<void> {
  try {
    const env: Envelope<T> = { t: Date.now(), v: value };
    await Preferences.set({ key: PREFIX + key, value: JSON.stringify(env) });
  } catch (err) {
    console.warn('cacheSet failed', key, err);
  }
}

export async function cacheGet<T>(key: string): Promise<{ value: T; age: number } | null> {
  try {
    const { value } = await Preferences.get({ key: PREFIX + key });
    if (!value) return null;
    const env = JSON.parse(value) as Envelope<T>;
    return { value: env.v, age: Date.now() - env.t };
  } catch {
    return null;
  }
}

export async function cacheRemove(key: string): Promise<void> {
  try {
    await Preferences.remove({ key: PREFIX + key });
  } catch { /* ignore */ }
}

/**
 * Fetch fresh data and cache it. If the fetch fails (offline / server down),
 * fall back to the last cached value. Throws only if there is no cache to fall
 * back on. `onStale` is invoked with the cached value+age when a fallback is used.
 */
export async function withCache<T>(
  key: string,
  fetcher: () => Promise<T>,
  opts?: { onStale?: (cached: T, age: number) => void },
): Promise<T> {
  try {
    const fresh = await fetcher();
    // Only cache truthy, non-empty results to avoid clobbering good data with []
    if (fresh != null) await cacheSet(key, fresh);
    return fresh;
  } catch (err) {
    const cached = await cacheGet<T>(key);
    if (cached) {
      opts?.onStale?.(cached.value, cached.age);
      return cached.value;
    }
    throw err;
  }
}
